import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2, Plus } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { supabase } from '../../../../../integrations/supabase/client';
import { agentsApi, AgentChannel } from '../../../services/agentsApi';
import { toast } from '@/hooks/use-toast';
import NewChannelModal from '../../../../channels/components/NewChannelModal';

interface AgentChannelsProps {
    agentId?: string;
}

interface WhatsAppChannel {
    id: string;
    numero_telefono: string;
    nombre_instancia: string;
    estado: string;
}

const AgentChannels: React.FC<AgentChannelsProps> = ({ agentId }) => {
    const [connectedChannels, setConnectedChannels] = useState<AgentChannel[]>([]);
    const [availableChannels, setAvailableChannels] = useState<WhatsAppChannel[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddChannel, setShowAddChannel] = useState(false);
    const [selectedNewChannel, setSelectedNewChannel] = useState('');
    const [showNewChannelModal, setShowNewChannelModal] = useState(false);

    useEffect(() => {
        if (agentId) {
            loadConnectedChannels();
            loadAvailableChannels();
        }
    }, [agentId]);

    const loadConnectedChannels = async () => {
        if (!agentId) return;

        try {
            console.log('[AgentChannels] 🔍 Cargando canales conectados del agente:', agentId);
            const channels = await agentsApi.getAgentChannels(agentId);
            console.log('[AgentChannels] ✅ Canales conectados:', channels);
            setConnectedChannels(channels);
        } catch (error) {
            console.error('[AgentChannels] ❌ Error cargando canales conectados:', error);
        }
    };

    const loadAvailableChannels = async () => {
        try {
            console.log('[AgentChannels] 🔍 Cargando canales de WhatsApp disponibles...');
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('whatsapp_connections')
                .select('*')
                .eq('user_id', user.id)
                .eq('estado', 'connected')
                .order('created_at', { ascending: false });

            if (error) throw error;
            console.log('[AgentChannels] ✅ Canales disponibles:', data?.length || 0);
            setAvailableChannels(data || []);
        } catch (error) {
            console.error('[AgentChannels] ❌ Error cargando canales disponibles:', error);
        }
    };

    const handleConnectChannel = async () => {
        if (!agentId || !selectedNewChannel) return;

        setLoading(true);
        try {
            console.log('[AgentChannels] 🔗 Conectando canal:', selectedNewChannel);

            const selectedChannelData = availableChannels.find(
                ch => ch.numero_telefono === selectedNewChannel
            );

            await agentsApi.connectChannel(
                agentId,
                selectedNewChannel,
                selectedChannelData?.nombre_instancia,
                connectedChannels.length === 0 // Primer canal es principal
            );

            // Actualizar agent_config con el nuevo número de teléfono
            await updateAgentConfigPhone(agentId, selectedNewChannel);

            console.log('[AgentChannels] ✅ Canal conectado exitosamente');
            toast.success('Canal conectado exitosamente');
            setShowAddChannel(false);
            setSelectedNewChannel('');
            await loadConnectedChannels();
        } catch (error: any) {
            console.error('[AgentChannels] ❌ Error conectando canal:', error);
            if (error.message?.includes('duplicate') || error.code === '23505') {
                toast.error('Este canal ya está conectado a otro agente');
            } else {
                toast.error('Error al conectar el canal');
            }
        } finally {
            setLoading(false);
        }
    };

    const updateAgentConfigPhone = async (agentId: string, numeroTelefono: string) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Buscar la configuración existente del agente
            const { data: existingConfig } = await supabase
                .from('agent_config')
                .select('id')
                .eq('agent_id', agentId)
                .eq('user_id', user.id)
                .maybeSingle();

            if (existingConfig) {
                // Actualizar el número de teléfono
                await supabase
                    .from('agent_config')
                    .update({ numero_telefono: numeroTelefono })
                    .eq('id', existingConfig.id);

                console.log('[AgentChannels] ✅ agent_config actualizado con número:', numeroTelefono);
            }
        } catch (error) {
            console.error('[AgentChannels] ⚠️ Error actualizando agent_config:', error);
            // No lanzar error, solo loggear
        }
    };

    const handleDisconnectChannel = async (channelId: string) => {
        setLoading(true);
        try {
            console.log('[AgentChannels] 🔌 Desconectando canal:', channelId);
            await agentsApi.disconnectChannel(channelId);
            console.log('[AgentChannels] ✅ Canal desconectado');
            toast.success('Canal desconectado');
            await loadConnectedChannels();
        } catch (error) {
            console.error('[AgentChannels] ❌ Error desconectando canal:', error);
            toast.error('Error al desconectar el canal');
        } finally {
            setLoading(false);
        }
    };

    const handleSetPrimary = async (channelId: string) => {
        if (!agentId) return;

        setLoading(true);
        try {
            console.log('[AgentChannels] ⭐ Marcando canal como principal:', channelId);

            // Obtener el número de teléfono del canal que se está marcando como principal
            const channel = connectedChannels.find(ch => ch.id === channelId);

            await agentsApi.setPrimaryChannel(agentId, channelId);

            // Actualizar agent_config con el número del canal principal
            if (channel) {
                await updateAgentConfigPhone(agentId, channel.numero_telefono);
            }

            console.log('[AgentChannels] ✅ Canal principal actualizado');
            toast.success('Canal principal actualizado');
            await loadConnectedChannels();
        } catch (error) {
            console.error('[AgentChannels] ❌ Error actualizando canal principal:', error);
            toast.error('Error al actualizar el canal principal');
        } finally {
            setLoading(false);
        }
    };

    // Filtrar canales que ya están conectados
    const unconnectedChannels = availableChannels.filter(
        available => !connectedChannels.some(
            connected => connected.numero_telefono === available.numero_telefono
        )
    );

    return (
        <div className="space-y-6 p-4">
            {/* Connected Channels Section */}
            {agentId && (
                <div className="bg-white rounded-lg border border-grey-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-foreground font-semibold text-base">
                            Canales Conectados ({connectedChannels.length})
                        </h3>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => setShowNewChannelModal(true)}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                            >
                                <Plus size={14} className="mr-1" />
                                Agregar Canal
                            </Button>
                            <Button
                                onClick={() => setShowAddChannel(!showAddChannel)}
                                variant="outline"
                                size="sm"
                                className="text-xs"
                            >
                                <RefreshCw size={14} className="mr-1" />
                                Conectar Canal
                            </Button>
                        </div>
                    </div>

                    {connectedChannels.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Número</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Instancia</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Principal</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {connectedChannels.map(channel => (
                                        <tr key={channel.id} className={channel.is_primary ? 'bg-primary/10' : ''}>
                                            <td className="px-4 py-3 text-sm font-medium text-foreground">
                                                {channel.numero_telefono}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-muted-foreground">
                                                {channel.nombre_instancia || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                    Conectado
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {channel.is_primary ? (
                                                    <span className="text-yellow-600 font-semibold">⭐ Canal Principal</span>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">Canal secundario</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right">
                                                <div className="inline-flex items-center gap-2">
                                                    {!channel.is_primary && (
                                                        <Button
                                                            onClick={() => handleSetPrimary(channel.id)}
                                                            variant="outline"
                                                            size="sm"
                                                            disabled={loading}
                                                            className="text-xs"
                                                        >
                                                            Hacer Principal
                                                        </Button>
                                                    )}
                                                    <Button
                                                        onClick={() => handleDisconnectChannel(channel.id)}
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={loading}
                                                        className="text-xs text-red-600 hover:bg-red-50"
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-800 font-medium mb-2">
                                ⚠️ Este agente no tiene canales de WhatsApp conectados
                            </p>
                            <p className="text-yellow-700 text-sm mb-3">
                                Conecta al menos un canal para que pueda recibir y enviar mensajes.
                            </p>
                        </div>
                    )}

                    {/* Add Channel Form */}
                    {showAddChannel && (
                        <div className="mt-4 p-4 border border-grey-200 rounded-lg bg-grey-50">
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Seleccionar Canal para Conectar
                            </label>
                            {unconnectedChannels.length > 0 ? (
                                <>
                                    <select
                                        value={selectedNewChannel}
                                        onChange={(e) => setSelectedNewChannel(e.target.value)}
                                        className="w-full px-3 py-2 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary mb-3"
                                    >
                                        <option value="">Selecciona un canal</option>
                                        {unconnectedChannels.map(channel => (
                                            <option key={channel.id} value={channel.numero_telefono}>
                                                {channel.numero_telefono} ({channel.nombre_instancia})
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={() => setShowAddChannel(false)}
                                            variant="outline"
                                            size="sm"
                                            disabled={loading}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            onClick={handleConnectChannel}
                                            size="sm"
                                            disabled={loading || !selectedNewChannel}
                                            className="bg-primary hover:bg-primary/90"
                                        >
                                            {loading ? 'Conectando...' : 'Conectar Canal'}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-sm text-muted-foreground">
                                    No hay más canales disponibles para conectar.
                                    {availableChannels.length === 0 && (
                                        <p className="mt-2">
                                            Ve a la sección <strong>Canales</strong> para crear uno nuevo.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* New Channel Modal */}
            <NewChannelModal
                open={showNewChannelModal}
                onClose={() => setShowNewChannelModal(false)}
                onSuccess={() => {
                    loadAvailableChannels();
                    toast.success('Canal agregado exitosamente');
                }}
            />

            {/* Info Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-blue-900 font-semibold text-sm mb-2">
                    ℹ️ Información sobre Canales
                </h3>
                <ul className="text-blue-800 text-xs space-y-1">
                    <li>• Un agente puede tener <strong>múltiples canales</strong> conectados</li>
                    <li>• El <strong>canal principal</strong> se usa por defecto para enviar mensajes</li>
                    <li>• Un canal <strong>solo puede estar en un agente a la vez</strong></li>
                    <li>• Para gestionar canales, visita la sección <strong>Canales</strong></li>
                </ul>
            </div>
        </div>
    );
};

export default AgentChannels;
